import { IllumiLogoMark } from "@src/components/illumi-logo-mark";
import { LoadingSpinnerFullContainer } from "@src/components/loading-spinner";
import clsx from "clsx";
import { AnimatePresence } from "framer-motion";
import { Suspense, lazy } from "react";
import { createPortal } from "react-dom";
import { AgentChatDrawerShell } from "./agent-chat-drawer-shell";
import { AgentChatHeader } from "./agent-chat-header";
import { AgentChatLayout } from "./agent-chat-layout";
import { AgentChatOverlayShell } from "./agent-chat-overlay-shell";
import { useRightDrawerTabStore } from "./right-drawer-tab-store";

const AgentChatPanel = lazy(() =>
	import("./components/agent-chat-panel").then((module) => ({
		default: module.AgentChatPanel,
	})),
);

const DesktopTerminalPanel = lazy(() =>
	import("@src/components/terminal/desktop-terminal-panel").then((module) => ({
		default: module.DesktopTerminalPanel,
	})),
);

type AgentChatDrawerProps = {
	isOpen: boolean;
	enterFromFullscreen?: boolean;
	onClose: () => void;
	onExpand?: () => void;
};

function AgentChatDrawerContent({
	isOpen,
	onClose,
	onExpand,
}: {
	isOpen: boolean;
	onClose: () => void;
	onExpand?: () => void;
}) {
	const { activeTab } = useRightDrawerTabStore();

	return (
		<div
			className="flex h-full min-h-0 flex-1 flex-col overflow-hidden"
			data-ui-id="agent-chat-drawer"
		>
			<AgentChatLayout
				header={
					<AgentChatHeader
						layout="drawer"
						onClose={onClose}
						onExpand={onExpand}
					/>
				}
			>
				<div
					className={clsx(
						"flex min-h-0 flex-1 flex-col overflow-hidden",
						activeTab !== "agent" && "hidden",
					)}
					id="agent-chat-drawer"
					role="tabpanel"
					aria-labelledby="drawer-tab-agent"
					data-ui-id="agent-chat-drawer-tabpanel"
				>
					<Suspense
						fallback={
							<div className="flex h-full w-full items-center justify-center">
								<IllumiLogoMark animated />
							</div>
						}
					>
						<AgentChatPanel />
					</Suspense>
				</div>

				<div
					className={clsx(
						"flex min-h-0 flex-1 flex-col overflow-hidden",
						activeTab !== "terminal" && "hidden",
					)}
					id="agent-terminal-drawer"
					role="tabpanel"
					aria-labelledby="drawer-tab-terminal"
					data-ui-id="agent-terminal-drawer"
				>
					<Suspense fallback={<LoadingSpinnerFullContainer />}>
						<DesktopTerminalPanel
							isVisible={isOpen && activeTab === "terminal"}
						/>
					</Suspense>
				</div>
			</AgentChatLayout>
		</div>
	);
}

export function AgentChatDrawer({
	isOpen,
	enterFromFullscreen = false,
	onClose,
	onExpand,
}: AgentChatDrawerProps) {
	const drawerContainer = document.getElementById(
		"agent-chat-drawer-container",
	);

	if (drawerContainer) {
		return createPortal(
			<AnimatePresence>
				{isOpen ? (
					<AgentChatDrawerShell
						key={enterFromFullscreen ? "from-fullscreen" : "from-edge"}
						enterFromFullscreen={enterFromFullscreen}
					>
						<AgentChatDrawerContent
							isOpen={isOpen}
							onClose={onClose}
							onExpand={onExpand}
						/>
					</AgentChatDrawerShell>
				) : null}
			</AnimatePresence>,
			drawerContainer,
		);
	}

	return createPortal(
		<AnimatePresence>
			{isOpen ? (
				<AgentChatOverlayShell key="overlay">
					<AgentChatDrawerContent
						isOpen={isOpen}
						onClose={onClose}
						onExpand={onExpand}
					/>
				</AgentChatOverlayShell>
			) : null}
		</AnimatePresence>,
		document.body,
	);
}

export {
	closeAgentChatDrawer,
	openAgentChatDrawer,
	toggleAgentChatDrawer,
} from "./agent-chat-drawer-controls";
